import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Exportar, { descargarArchivo, exportarExcelGenerico } from '../components/reportes/Exportar';
import EncabezadoImpresion from '../components/reportes/EncabezadoImpresion';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha, hoyCaracas } from '../utils/format';
import BotonAyuda from '../components/BotonAyuda';

function primerDiaMes() {
  return `${hoyCaracas().slice(0, 8)}01`;
}

// Desde Impuestos llega embebida con el mes del resumen (desdeInicial/hastaInicial).
export default function LibroVentas({ embebido = false, desdeInicial, hastaInicial } = {}) {
  const { empresaActiva, empresas } = useAuth();
  const empresaNombre = empresas?.find((e) => String(e.empresa_id) === String(empresaActiva))?.empresa_nombre;
  const [desde, setDesde] = useState(desdeInicial || primerDiaMes());
  const [hasta, setHasta] = useState(hastaInicial || hoyCaracas);
  const [vista, setVista] = useState('fiscal'); // 'fiscal' | 'no_fiscal'
  const [detalle, setDetalle] = useState([]);
  const [totales, setTotales] = useState([]);
  const [noFiscales, setNoFiscales] = useState([]);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    if (!empresaActiva) return;
    setCargando(true);
    const req = vista === 'fiscal'
      ? api.get('/reportes/libro-ventas', { params: { desde, hasta } })
          .then((res) => { setDetalle(res.data.detalle); setTotales(res.data.totales); })
      : api.get('/reportes/documentos-no-fiscales', { params: { desde, hasta, tipo: 'venta' } })
          .then((res) => setNoFiscales(res.data.datos));
    req.finally(() => setCargando(false));
  }, [empresaActiva, desde, hasta, vista]);

  async function exportarExcel() {
    if (vista === 'no_fiscal') return exportarExcelGenerico(contenidoNoFiscal());
    const { data } = await api.get('/reportes/libro-ventas/excel', { params: { desde, hasta }, responseType: 'blob' });
    descargarArchivo(data, `libro-ventas${empresaNombre ? `-${empresaNombre}` : ''}-${desde}-${hasta}.xlsx`);
  }

  // "Fuera del libro fiscal" no tiene Excel propio en el servidor: se arma
  // con lo que se ve en pantalla.
  function contenidoNoFiscal() {
    return {
      titulo: 'Libro de ventas fuera del libro fiscal',
      detalle: `Del ${formatFecha(desde)} al ${formatFecha(hasta)}`,
      hojas: [{
        nombre: 'Fuera del libro',
        columnas: [{ titulo: 'Fecha', tipo: 'fecha' }, { titulo: 'Cliente' }, { titulo: 'N° documento' }, { titulo: 'Moneda' }, { titulo: 'Total', tipo: 'monto' }, { titulo: 'Notas' }],
        filas: noFiscales.map((f) => [String(f.fecha).slice(0, 10), f.parte_nombre, f.numero, f.moneda, Number(f.total) || 0, f.notas || ''])
      }]
    };
  }


  return (
    <div className="space-y-6">
      <EncabezadoImpresion titulo="Libro de ventas" detalle={`${vista === 'fiscal' ? 'Libro fiscal' : 'Fuera del libro fiscal'} · del ${formatFecha(desde)} al ${formatFecha(hasta)}`} />
      <div className="flex items-center justify-between flex-wrap gap-3 print:hidden">
        <div>
          {!embebido && <h1 className="text-2xl font-bold text-slate-800">Libro de Ventas<BotonAyuda /></h1>}
          <p className="text-sm text-slate-500">Formato SENIAT — solo documentos fiscales. #34</p>
        </div>
        <Exportar onExcel={exportarExcel} />
      </div>

      <div className="flex items-end gap-3 flex-wrap print:hidden">
        <div>
          <label className="block text-xs text-slate-500 mb-1">Desde</label>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="border rounded-md px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Hasta</label>
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="border rounded-md px-2 py-1.5 text-sm" />
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-md p-1 ml-auto">
          <button
            onClick={() => setVista('fiscal')}
            className={`px-3 py-1.5 rounded text-sm ${vista === 'fiscal' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500'}`}
          >
            Libro fiscal
          </button>
          <button
            onClick={() => setVista('no_fiscal')}
            className={`px-3 py-1.5 rounded text-sm ${vista === 'no_fiscal' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500'}`}
          >
            Fuera del libro fiscal
          </button>
        </div>
      </div>

      {cargando ? (
        <p className="text-slate-500">Cargando...</p>
      ) : vista === 'fiscal' ? (
        <>
          <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs">
                <tr>
                  <th className="text-left p-2">Fecha</th>
                  <th className="text-left p-2">RIF</th>
                  <th className="text-left p-2">Nombre</th>
                  <th className="text-left p-2">N° Documento</th>
                  <th className="text-right p-2">Total</th>
                  <th className="text-right p-2">Base imponible</th>
                  <th className="text-right p-2">IVA</th>
                  <th className="text-right p-2">IVA retenido</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {detalle.length === 0 ? (
                  <tr><td colSpan={8} className="text-center text-slate-400 p-6">Sin documentos fiscales en ese rango.</td></tr>
                ) : detalle.map((f) => (
                  <tr key={`${f.id}-${f.maquina_fiscal || ''}`}>
                    <td className="p-2">{formatFecha(f.fecha)}</td>
                    <td className="p-2">{f.rif || '—'}</td>
                    <td className="p-2">{f.nombre}</td>
                    <td className="p-2">{f.nro_documento || (f.maquina_fiscal ? `Z ${f.reporte_z || ''} (${f.maquina_fiscal})` : '—')}</td>
                    <td className="p-2 text-right">{formatMonto(f.total)}</td>
                    <td className="p-2 text-right">{formatMonto(f.base_imponible_ventas)}</td>
                    <td className="p-2 text-right">{formatMonto(f.iva_ventas)}</td>
                    <td className="p-2 text-right">{f.iva_retenido != null ? formatMonto(f.iva_retenido) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-4">
            <h2 className="text-sm font-medium text-slate-700 mb-3">Totales por alícuota</h2>
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500">
                <tr>
                  <th className="text-left p-1">Categoría</th>
                  <th className="text-right p-1">Base imponible</th>
                  <th className="text-right p-1">Débito fiscal</th>
                  <th className="text-right p-1">IVA retenido</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {totales.filter((t) => t.base_imponible || t.iva || t.iva_retenido).map((t) => (
                  <tr key={t.categoria}>
                    <td className="p-1">{t.categoria}</td>
                    <td className="p-1 text-right">{formatMonto(t.base_imponible)}</td>
                    <td className="p-1 text-right">{formatMonto(t.iva)}</td>
                    <td className="p-1 text-right">{formatMonto(t.iva_retenido)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs">
              <tr>
                <th className="text-left p-2">Fecha</th>
                <th className="text-left p-2">Cliente</th>
                <th className="text-left p-2">N° Documento</th>
                <th className="text-right p-2">Total</th>
                <th className="text-left p-2">Notas</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {noFiscales.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-slate-400 p-6">Sin ventas no fiscales en ese rango.</td></tr>
              ) : noFiscales.map((f) => (
                <tr key={f.id}>
                  <td className="p-2">{formatFecha(f.fecha)}</td>
                  <td className="p-2">{f.parte_nombre}</td>
                  <td className="p-2">{f.numero}</td>
                  <td className="p-2 text-right">{formatMonto(f.total)} {f.moneda}</td>
                  <td className="p-2 text-slate-500">{f.notas || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
